import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ContractInsightsData } from '@/pages/dashboard/ContractInsights';
import { StatCard, CurrencyDisplay } from '@/components/ui/stat-card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ListPagination } from '@/components/ListPagination';
import { Badge } from '@/components/ui/badge';
import { Search } from 'lucide-react';
import { formatBusinessDate, formatBusinessTime } from '@/lib/format';
import { useDebounce } from '@/hooks/useDebounce';
import { cn } from '@/lib/utils';

interface Props {
    data: ContractInsightsData;
}

const PAGE_SIZE = 20;

interface TransactionRecord {
    id: number;
    amount: number;
    transaction_at: string;
    description: string | null;
    transaction_type: string;
    note: string | null;
    total_count: number;
}

const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(amount);
};

const getTypeBadge = (type: string) => {
    switch (type) {
        case 'deposit':
            return <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200">Deposit</Badge>;
        case 'deposit_refund':
            return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Deposit Refund</Badge>;
        case 'loan_repayment':
            return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Advance</Badge>;
        case 'insurance_loan_repayment':
            return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Ins. Advance</Badge>;
        case 'insurance_loan_deposit':
            return <Badge variant="outline" className="bg-pink-50 text-pink-700 border-pink-200">Ins. Deposit</Badge>;
        default:
            return null; // repayment - no badge needed, it's the default
    }
};

export default function ContractInsightPayments({ data }: Props) {
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, 300);

    const contractId = data.contract?.id;

    // Reset to page 1 when search changes
    useEffect(() => { setCurrentPage(1); }, [debouncedSearch]);

    // Fetch transactions using new RPC
    const { data: transactionsData, isLoading } = useQuery({
        queryKey: ['contract-transactions', contractId, currentPage, debouncedSearch],
        queryFn: async () => {
            if (!contractId) return { transactions: [], total: 0 };

            const { data, error } = await supabase.rpc('get_contract_transactions', {
                p_contract_id: contractId,
                p_search: debouncedSearch || null,
                p_page: currentPage,
                p_page_size: PAGE_SIZE,
            });

            if (error) throw error;
            const records = (data || []) as TransactionRecord[];
            return {
                transactions: records,
                total: records[0]?.total_count || 0
            };
        },
        enabled: !!contractId,
    });

    // Fetch all transactions for stats (without pagination)
    const { data: allTransactions } = useQuery({
        queryKey: ['contract-transactions-all', contractId],
        queryFn: async () => {
            if (!contractId) return [];

            const { data, error } = await supabase.rpc('get_contract_transactions', {
                p_contract_id: contractId,
                p_search: null,
                p_page: 1,
                p_page_size: 10000, // Get all for stats
            });

            if (error) throw error;
            return (data || []) as TransactionRecord[];
        },
        enabled: !!contractId,
    });

    // Compute stats
    const stats = useMemo(() => {
        if (!allTransactions || allTransactions.length === 0) {
            return {
                totalPayments: 0,
                paymentCount: 0,
                averagePayment: 0,
                lastPayment: null as Date | null,
                largestPayment: 0,
            };
        }

        // Amounts are signed: deposit refunds arrive negative so the total nets out.
        // Count, average, largest and last payment describe money coming in, so refund
        // rows are excluded from all of them (rows arrive newest first).
        let totalPayments = 0;
        let nonRefundTotal = 0;
        let paymentCount = 0;
        let largestPayment = 0;
        let lastPayment: Date | null = null;
        for (const t of allTransactions) {
            totalPayments += t.amount;
            if (t.transaction_type === 'deposit_refund') continue;
            nonRefundTotal += t.amount;
            paymentCount += 1;
            if (t.amount > largestPayment) largestPayment = t.amount;
            if (!lastPayment && t.transaction_at) lastPayment = new Date(t.transaction_at);
        }
        const averagePayment = paymentCount > 0 ? nonRefundTotal / paymentCount : 0;

        return {
            totalPayments,
            paymentCount,
            averagePayment,
            lastPayment,
            largestPayment,
        };
    }, [allTransactions]);

    const transactions = transactionsData?.transactions || [];
    const totalCount = transactionsData?.total || 0;
    const totalPages = Math.ceil(totalCount / PAGE_SIZE);

    return (
        <div className="space-y-6">
            {/* Payment Summary Cards */}
            <div className="grid gap-4 md:grid-cols-5">
                <StatCard
                    title="Total Payments"
                    value={
                        <>
                            {/* CurrencyDisplay renders the magnitude — sign a negative net explicitly */}
                            {stats.totalPayments < 0 && '−'}
                            <CurrencyDisplay amount={stats.totalPayments} />
                        </>
                    }
                    description="The cumulative sum of all payment transactions recorded for this contract, including deposits, advance repayments, and regular instalments, less any deposit refunded to the driver."
                />
                <StatCard
                    title="Payment Count"
                    value={stats.paymentCount}
                    description="The total number of individual payment transactions made towards this contract over its lifetime. Deposit refunds are not counted as payments."
                />
                <StatCard
                    title="Average Payment"
                    value={<CurrencyDisplay amount={stats.averagePayment} />}
                    description={`The mean payment amount calculated by dividing the payments received — deposit refunds excluded — by the number of those payments (${stats.paymentCount}).`}
                />
                <StatCard
                    title="Last Payment"
                    value={stats.lastPayment ? formatBusinessDate(stats.lastPayment) : 'N/A'}
                    description="The date and time of the most recent payment transaction recorded for this contract."
                />
                <StatCard
                    title="Largest Payment"
                    value={<CurrencyDisplay amount={stats.largestPayment} />}
                    description="The highest single payment amount received in one transaction for this contract."
                />
            </div>

            {/* Transaction List */}
            <div className="space-y-4">
                <div className="flex items-center justify-between gap-4">
                    <div className="relative w-full sm:w-64">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Search by description..."
                            className="pl-9"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {totalPages > 0 && (
                        <ListPagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                            totalItems={totalCount}
                        />
                    )}
                </div>

                {isLoading ? (
                    <Skeleton className="h-48 w-full" />
                ) : transactions.length > 0 ? (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Date</TableHead>
                                <TableHead>Amount</TableHead>
                                <TableHead>Description</TableHead>
                                <TableHead>Notes</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {transactions.map((transaction) => {
                                return (
                                    <TableRow key={transaction.id}>
                                        <TableCell className="whitespace-nowrap">
                                            <div>
                                                <div className="font-medium">{formatBusinessDate(transaction.transaction_at)}</div>
                                                <div className="text-xs text-muted-foreground">{formatBusinessTime(transaction.transaction_at)}</div>
                                            </div>
                                        </TableCell>
                                        <TableCell className={cn(
                                            "font-medium",
                                            transaction.amount >= 0 ? "text-green-600" : "text-red-600"
                                        )}>
                                            {formatCurrency(transaction.amount)}
                                        </TableCell>
                                        <TableCell className="max-w-[250px]">
                                            <div className="flex items-start gap-2">
                                                <span className="break-words whitespace-normal">{transaction.description || '-'}</span>
                                                {getTypeBadge(transaction.transaction_type)}
                                            </div>
                                        </TableCell>
                                        <TableCell className="w-[250px] min-w-[250px] max-w-[250px] break-words whitespace-normal text-muted-foreground">
                                            {transaction.note || '-'}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                ) : (
                    <p className="text-center text-muted-foreground py-8">
                        {searchTerm ? 'No transactions match your search' : 'No transactions found'}
                    </p>
                )}
            </div>
        </div>
    );
}
